<script lang="ts">
  import splitAndTrim from '../../utils/split-and-trim';
  import Chip from '../chip/Chip.svelte';
  import Modal from '../modal/Modal.svelte';
  import Tooltip from '../tooltip/Tooltip.svelte';
  import InlineSVG from 'svelte-inline-svg';
  import helpIcon from '../../assets/icons/help.svg';

  export let label = '';
  export let tooltipText = '';
  export let placeholder = '';
  export let value: string;
  export let id = '';
  export let required = false;
  export let editable = true;
  export let availableTags: Array<{value: string; label?: string}>;
  let inputElement: HTMLInputElement;

  let tags = availableTags.filter(availableTag =>
    splitAndTrim(value).includes(availableTag.value)
  );
  let tagValue: string;
  let highlightedTagIndex = -1;
  let suggestions: Array<{value: string; label?: string}> = [];
  let showSuggestions = false;
  let showModal = false;
  let isDuplicateTag = false;

  // Handle keyboard controls
  function handleKeydown(event: KeyboardEvent) {
    // Highlight previous tag when user presses arrow up
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      highlightedTagIndex = Math.max(highlightedTagIndex - 1, -1);
    }

    // Highlight next tag when user presses arrow down
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      highlightedTagIndex = Math.min(
        highlightedTagIndex + 1,
        suggestions.length - 1
      );
    }

    // Add tag when user presses enter
    if (event.key === 'Enter') {
      event.preventDefault();
      const tagToAdd = suggestions[highlightedTagIndex]
        ? [suggestions[highlightedTagIndex]]
        : splitAndTrim(tagValue).map(v => ({value: v}));
      if (
        !isNewTag(
          suggestions.map(({value}) => value),
          tagToAdd.map(({value}) => value)
        )
      ) {
        addTags(tagToAdd);
      } else {
        showConfirmModal();
      }
    }

    // Remove last tag when user presses backspace
    if (event.key === 'Backspace' && !tagValue) {
      tags.pop();
      tags = tags;
      value = tags.map(tag => tag.value).join(',');
    }
  }

  // Focus input element
  function focusInput() {
    inputElement && inputElement.focus();
    showSuggestions = true;
  }

  // Add remaining input to tags on blur
  function handleBlur() {
    if (!showModal) {
      if (editable && tagValue) {
        if (
          !isNewTag(
            suggestions.map(({value}) => value),
            [tagValue]
          )
        ) {
          addTags(splitAndTrim(tagValue).map(v => ({value: v})));
        } else {
          showConfirmModal();
        }
      } else {
        tagValue = '';
      }
      showSuggestions = false;
    }
  }

  function handleFocus() {
    showSuggestions = true;
    getSuggestions();
  }

  function handleClick() {
    showSuggestions = true;
    getSuggestions();
  }

  // Add tags to the list
  function addTags(valuesToAdd: Array<{value: string; label?: string}>) {
    if (!valuesToAdd) {
      return;
    }

    const tagsToAdd = valuesToAdd
      // Filter out tags that are already in the list
      .filter(
        valueToAdd =>
          !tags
            .map(tag => tag.value.toLowerCase())
            .includes(valueToAdd.value.toLowerCase())
      )
      // Use upper and lower case according to available tags if it's a known tag
      .map(
        valueToAdd =>
          availableTags.find(
            availableTag =>
              availableTag.value.toLowerCase() ===
              valueToAdd.value.toLowerCase()
          ) || valueToAdd
      )
      .map(valueToAdd => ({
        value: valueToAdd.value.replace(/(^[a-z])|(\s[a-z])/g, c =>
          c.toUpperCase()
        ),
        ...(valueToAdd.label ? {label: valueToAdd.label} : {})
      }));

    // Reset typed input and suggestions
    tagValue = '';
    suggestions = [];
    highlightedTagIndex = -1;

    if (!tagsToAdd.length) {
      return;
    }

    tags = tags.concat(tagsToAdd);
    value = tags.map(tag => tag.value).join(',');
  }

  // Check if input has been entered before
  function isNewTag(allTags: Array<string>, tagsToAdd: Array<string>) {
    tags.forEach(tag => {
      for (const tagToAdd of tagsToAdd) {
        if (
          !tagToAdd.localeCompare(tag.value, undefined, {
            sensitivity: 'base'
          })
        ) {
          isDuplicateTag = true;
          return true;
        }
      }
    });
    return !allTags.some(tag => tagsToAdd.includes(tag));
  }

  // Show modal
  function showConfirmModal() {
    showModal = true;
  }

  // Close Modal and cancel action
  export const cancelNewTagValue = () => {
    tagValue = '';
    suggestions = [];
    highlightedTagIndex = -1;
    showModal = false;
    isDuplicateTag = false;
  };

  // Close Modal and add tag
  function confirmNewTagValue() {
    addTags(splitAndTrim(tagValue).map(v => ({value: v})));
    showModal = false;
  }

  // Remove the tag with the given index from the list
  function removeTag(tagIndex: number) {
    tags.splice(tagIndex, 1);
    tags = tags;
    value = tags.map(tag => tag.value).join(',');

    focusInput();
  }

  // Get suggestions for the current input
  function getSuggestions() {
    if (!availableTags.length) {
      return;
    }

    highlightedTagIndex = -1;

    suggestions = availableTags.filter(
      availableTag =>
        !tags.some(tag => tag.value === availableTag.value) &&
        (!tagValue ||
          availableTag.value.toLowerCase().includes(tagValue.toLowerCase()) ||
          availableTag.label?.toLowerCase().includes(tagValue.toLowerCase()))
    );
  }
</script>

<style lang="postcss">
  .container {
    display: grid;
    gap: 0.25rem;
    margin-bottom: 1.5rem;
  }

  .container > div {
    position: relative;
  }

  @media (min-width: 768px) {
    .container {
      grid-template-columns: 1fr 2fr 1fr;
      align-items: start;
      gap: 1.5rem;
    }

    .container > div:first-child {
      display: flex;
      align-items: center;
      justify-content: end;
      min-height: var(--ubilabs-input-height);
    }
  }

  .realValueInput {
    z-index: -1;
    position: absolute;
    opacity: 0;
  }

  .input {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    min-height: var(--ubilabs-input-height);
    padding: 0.1875rem 0.375rem;
    background-color: var(--ubilabs-input-background-color);
    border: var(--ubilabs-input-border);
    border-radius: var(--ubilabs-input-border-radius);
    outline: none;
    color: var(--ubilabs-input-color);
    font-family: var(--ubilabs-font-family);
    font-size: var(--ubilabs-input-font-size);
    line-height: var(--ubilabs-input-line-height);
  }

  .input.focused {
    box-shadow: var(--ubilabs-focus-box-shadow);
  }

  .input > input {
    flex-grow: 1;
    width: auto;
    min-width: var(--ubilabs-input-width);
    height: var(--ubilabs-chip-height);
    margin: var(--ubilabs-chip-margin);
    padding: 0 0.375rem;
    border: none;
    background: none;
  }

  .input > input:focus-visible {
    box-shadow: none;
  }

  .input > :global(div) {
    /* margin: 0.5rem var(--ubilabs-chip-margin-horizontal); */
  }

  .suggestions {
    z-index: 10;
    position: absolute;
    width: 100%;
    max-height: var(--ubilabs-dropdown-options-height);
    overflow-y: auto;
    margin: 0;
    padding: var(--ubilabs-dropdown-options-padding);
    background-color: var(--ubilabs-dropdown-background-color);
    border: var(--ubilabs-dropdown-options-border);
    border-top: none;
    border-bottom-right-radius: var(--ubilabs-dropdown-border-radius);
    border-bottom-left-radius: var(--ubilabs-dropdown-border-radius);
    box-shadow: var(--ubilabs-box-shadow);
  }

  .suggestions > li {
    padding: var(--ubilabs-dropdown-option-padding);
  }

  .suggestions > li:hover,
  .suggestions > li.highlighted {
    background-color: var(--ubilabs-dropdown-option-hover-background-color);
  }

  .newTag {
    color: var(--ubilabs-gray-dark-2);
  }

  .emptyMessage {
    color: var(--ubilabs-gray-dark-2);
    pointer-events: none;
  }

  :global(.tagsInputTip) {
    margin-left: 0.5rem;
    color: var(--ubilabs-label-color);
  }
</style>

{#if showModal}
  <Modal
    actions={isDuplicateTag
      ? [{label: 'Cancel', clickHandler: cancelNewTagValue}]
      : [
          {label: 'Cancel', clickHandler: cancelNewTagValue},
          {label: 'Confirm', clickHandler: confirmNewTagValue}
        ]}
    modalTitle={isDuplicateTag
      ? `'${tagValue}' has already been added to the list`
      : `Are you sure you want to add '${tagValue}' to the list?`}
    onBackdropClick={cancelNewTagValue}
    content={isDuplicateTag
      ? 'A tag cannot be added more than once.'
      : 'Sure you did not find anything else appropriate in the dropdown? 🙂 Thanks for checking.'} />
{/if}
<div class="container {$$props.class}">
  <div>
    {#if label}
      <label for={id}>{label}{required ? '*' : ''}</label>
    {/if}
    {#if tooltipText}
      <Tooltip position="bottom" openOnClick={true} class="tagsInputTip">
        <div slot="trigger">
          <InlineSVG src={helpIcon} />
        </div>
        <div slot="content">
          {tooltipText}
        </div>
      </Tooltip>
    {/if}
  </div>
  <div>
    <!-- Input needed for browser's native form validation -->
    <input
      class="realValueInput"
      type="text"
      name={`real-${id}`}
      {value}
      {required}
      on:focus={focusInput} />

    <div class="input {showSuggestions ? 'focused' : ''}">
      {#if tags.length}
        {#each tags as tag, index}
          <Chip onRemove={() => removeTag(index)}>
            {tag.label || tag.value}
          </Chip>
        {/each}
      {/if}

      <input
        type="text"
        autocomplete="off"
        {placeholder}
        name={id}
        {id}
        bind:value={tagValue}
        on:keydown={handleKeydown}
        on:input={getSuggestions}
        on:focus={handleFocus}
        on:blur={handleBlur}
        on:click|self={handleClick}
        bind:this={inputElement} />
    </div>

    {#if showSuggestions && (suggestions.length || tagValue)}
      <ul class="suggestions">
        {#each suggestions as suggestion, index}
          <li
            class:highlighted={index === highlightedTagIndex}
            value={suggestion.value}
            on:mousedown|preventDefault={() => {
              addTags([suggestion]);
            }}>
            {suggestion.label || suggestion.value}
          </li>
        {/each}
        {#if editable && tagValue}
          <li
            class="newTag"
            on:mousedown|preventDefault={() => {
              if (
                !isNewTag(
                  suggestions.map(({value}) => value),
                  [tagValue]
                )
              ) {
                addTags(splitAndTrim(tagValue).map(v => ({value: v})));
              } else {
                showConfirmModal();
              }
            }}>
            "{tagValue}" einfügen
          </li>
        {/if}
        {#if !editable && !suggestions.length}
          <li class="emptyMessage">No matching entries found.</li>
        {/if}
      </ul>
    {/if}
  </div>
</div>
